import type { ScalarHistogramResult } from '@sharefe/share3d-engine'

export interface ScalarHistogramBar {
  readonly index: number
  readonly percent: number
}

/** 直方图分桶 → CSS 高度百分比；空数据返回空数组。 */
export function buildScalarHistogramBars(bins: readonly number[]): ScalarHistogramBar[] {
  const max = bins.reduce((current, bin) => (bin > current ? bin : current), 0)
  if (max <= 0) return bins.map((_, index) => ({ index, percent: 0 }))
  return bins.map((bin, index) => ({
    index,
    percent: Math.max(2, Math.round((bin / max) * 100)),
  }))
}

/** 直方图展示区间；无数据时返回 null。 */
export function resolveScalarHistogramRange(
  result: ScalarHistogramResult | null
): { min: number; max: number } | null {
  if (!result) return null
  const { min, max } = result
  if (!Number.isFinite(min) || !Number.isFinite(max)) return null
  return { min, max }
}

/** 归一化过滤区间：非法返回 null，倒置自动交换。 */
export function normalizeScalarRange(
  min: number,
  max: number
): { min: number; max: number } | null {
  if (!Number.isFinite(min) || !Number.isFinite(max)) return null
  return min <= max ? { min, max } : { min: max, max: min }
}

/** 单值紧凑显示，供卡片区间文案使用。 */
export function formatScalarValue(value: number): string {
  if (!Number.isFinite(value)) return '—'
  const magnitude = Math.abs(value)
  if (magnitude >= 1_000_000) return `${(value / 1_000_000).toFixed(2)}M`
  if (magnitude >= 1_000) return `${(value / 1_000).toFixed(2)}K`
  if (magnitude >= 1) return value.toFixed(3)
  return value.toPrecision(3)
}

export function formatScalarRange(min: number, max: number): string {
  if (!Number.isFinite(min) || !Number.isFinite(max)) return '—'
  return `${formatScalarValue(min)} ~ ${formatScalarValue(max)}`
}
